import type { ReactNode } from 'react';

import { Card, cn, type StatusTone } from '@giyus/ui';

export interface KpiCardProps {
  /** Nome do indicador (ex.: "Estudos ativos"). */
  readonly label: ReactNode;
  /** Valor já formatado pelo chamador (i18n; números em `font-data`). */
  readonly value: ReactNode;
  /** Contexto curto sob o valor (ex.: "de 12 cadastrados"). */
  readonly description?: ReactNode;
  readonly icon?: ReactNode;
  /** Tom do valor; nunca é o único sinal: acompanhe de `toneLabel` (texto para leitores de tela). */
  readonly tone?: StatusTone;
  readonly toneLabel?: string;
  /** Rodapé (ex.: link "Ver estudos"). */
  readonly footer?: ReactNode;
  readonly className?: string;
}

const VALUE_TONE: Record<StatusTone, string> = {
  success: 'text-success-subtle-foreground',
  warning: 'text-warning-subtle-foreground',
  danger: 'text-danger-subtle-foreground',
  info: 'text-info-subtle-foreground',
  neutral: 'text-heading',
};

/** Indicador (KPI): par rótulo/valor semântico (`dl`) em um card; o tom colore o valor e ganha texto para leitores de tela. */
export function KpiCard({
  label,
  value,
  description,
  icon,
  tone = 'neutral',
  toneLabel,
  footer,
  className,
}: KpiCardProps) {
  return (
    <Card data-slot="kpi-card" data-tone={tone} className={cn('gap-2 p-4', className)}>
      <dl className="flex flex-col gap-1">
        <dt className="flex items-center gap-2 text-body-sm text-muted-foreground">
          {icon ? (
            <span aria-hidden="true" className="flex shrink-0 [&>svg]:size-4">
              {icon}
            </span>
          ) : null}
          {label}
        </dt>
        <dd className={cn('font-data text-headline font-semibold', VALUE_TONE[tone])}>
          {toneLabel ? <span className="sr-only">{toneLabel}: </span> : null}
          {value}
        </dd>
      </dl>
      {description ? <p className="text-body-sm text-foreground-secondary">{description}</p> : null}
      {footer ? <div className="mt-auto pt-1 text-body-sm">{footer}</div> : null}
    </Card>
  );
}
